import axios from 'axios'
import { getCompanyToken, useAuthStore } from 'shared/store/authStore'

function getApiBase(): string {
  const env = (import.meta.env.VITE_API_URL ?? '').trim()
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return ''
  }
  return env
}
const API_URL = getApiBase()

export const api = axios.create({
  baseURL: API_URL ? `${API_URL}/api/v1/company` : '/api/v1/company',
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use((config) => {
  const token = getCompanyToken()
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      useAuthStore.getState().logoutCustomer()
      window.location.href = '/dashboard'
    }
    return Promise.reject(err)
  },
)

export const companyAuth = {
  login: (email: string, password: string) =>
    api.post<{
      access_token: string
      user_id: string
      email: string
      role: string
      company_id: string
      company_name: string
    }>('/login', { email, password }),
  register: (data: {
    email: string
    password: string
    company_name: string
    tin: string
    business_unit?: string
    address?: string
  }) =>
    api.post<{ message: string; company_id: string }>('/register', data),
  me: () =>
    api.get<{
      id: string
      name: string
      tin: string
      business_unit: string | null
      address: string | null
      is_active: string
      is_verified: string
      locale: string
      created_at: string
      updated_at: string
    }>('/me'),
  updateMe: (data: { name?: string; address?: string; locale?: string }) => api.patch('/me', data),
}

export const companyDashboard = {
  overview: () =>
    api.get<{
      period_start: string
      period_end: string
      total_transactions: number
      total_revenue: number
      total_fees: number
      net_amount: number
      average_transaction_value: number
      next_payout_date: string | null
      estimated_payout_amount: number | null
      days_until_payout: number | null
      recent_transactions: Array<{
        id: string
        fiscal_number: string
        is_internal_receipt?: boolean
        amount: number
        currency: string
        status: string
        created_at: string | null
      }>
      daily_trend: Array<{ date: string; transactions: number; volume: number }>
    }>('/dashboard'),
}

export type TransactionItem = {
  id: string
  receipt_id: string | null
  internal_receipt_id: string | null
  is_internal_receipt: boolean
  fiscal_number: string
  iic: string | null
  amount: number
  bank_fee: number
  platform_fee: number
  net_amount: number
  currency: string
  status: string
  customer_email: string | null
  payment_method: string | null
  created_at: string
}

export const companyTransactions = {
  list: (params?: {
    status?: string
    date_from?: string
    date_to?: string
    search?: string
    skip?: number
    limit?: number
    sort?: string
    order?: string
  }) =>
    api.get<{ items: TransactionItem[]; total: number }>('/transactions', { params }),
  get: (id: string) =>
    api.get<{
      id: string
      receipt_id: string | null
      internal_receipt_id: string | null
      is_internal_receipt: boolean
      fiscal_number: string
      iic: string | null
      amount: number
      bank_fee: number
      platform_fee: number
      net_amount: number
      currency: string
      status: string
      customer_email: string | null
      customer_phone: string | null
      payment_method: string | null
      monri_transaction_id: string | null
      monri_order_number: string | null
      created_at: string
    }>(`/transactions/${id}`),
}

export const companyPayouts = {
  list: (params?: { status?: string; date_from?: string; date_to?: string; skip?: number; limit?: number }) =>
    api.get<{
      items: Array<{
        id: string
        period_start: string
        period_end: string
        total_amount: number
        total_bank_fee: number
        total_platform_fee: number
        net_amount: number
        transaction_count: number
        currency: string
        status: string
        reference: string | null
        processed_at: string | null
        created_at: string
      }>
      total: number
    }>('/payouts', { params }),
  current: () =>
    api.get<{
      period_start: string
      period_end: string
      transaction_count: number
      total_revenue: number
      total_fees: number
      estimated_net_amount: number
      next_payout_date: string | null
      days_until_payout: number | null
    }>('/payouts/current'),
  get: (id: string) =>
    api.get<{
      id: string
      period_start: string
      period_end: string
      total_amount: number
      total_bank_fee: number
      total_platform_fee: number
      net_amount: number
      transaction_count: number
      currency: string
      status: string
      reference: string | null
      processed_at: string | null
      failure_reason: string | null
      created_at: string
      transactions: Array<Record<string, unknown>>
    }>(`/payouts/${id}`),
}

export type NotificationPreferences = {
  notification_email: string | null
  notify_on_payment_email: boolean
  webhook_url: string | null
  webhook_secret: string | null
  notify_on_payment_webhook: boolean
  registered_email: string
}

export type NotificationLogItem = {
  id: string
  payment_id: string
  delivery_type: 'email' | 'webhook'
  attempt_number: number
  request_payload: Record<string, unknown> | null
  request_headers: Record<string, unknown> | null
  response_status: number | null
  response_body: string | null
  success: boolean
  error_message: string | null
  created_at: string | null
}

export const companySettings = {
  feeMode: () =>
    api.get<{ fee_mode: 'company_absorbs' | 'customer_pays' }>('/settings/fee-mode'),
  updateFeeMode: (fee_mode: 'company_absorbs' | 'customer_pays') =>
    api.put<{ fee_mode: 'company_absorbs' | 'customer_pays' }>('/settings/fee-mode', { fee_mode }),
  bankAccount: () =>
    api.get<{ bank_account_number: string | null; bank_name: string | null }>('/settings/bank-account'),
  updateBankAccount: (bank_account_number: string | null) =>
    api.put<{ bank_account_number: string | null; bank_name: string | null }>('/settings/bank-account', { bank_account_number }),
  identifyBank: (account_number: string) =>
    api.get<{ bank_account_number: string; bank_name: string | null; error?: string }>(`/settings/identify-bank/${encodeURIComponent(account_number)}`),
  feeStructure: () =>
    api.get<{
      bank_fee_type: string
      bank_fee_value: number | null
      bank_fee_percent: number | null
      bank_fee_fixed: number | null
      platform_fee_type: string
      platform_fee_percent: number | null
      platform_fee_fixed: number | null
      platform_fee_tiers: unknown
    }>('/settings/fee-structure'),
  users: () =>
    api.get<{ users: Array<{ id: string; email: string; role: string }> }>('/settings/users'),
  notifications: () =>
    api.get<NotificationPreferences>('/settings/notifications'),
  updateNotifications: (data: {
    notification_email?: string | null
    notify_on_payment_email?: boolean
    webhook_url?: string | null
    webhook_secret?: string | null
    notify_on_payment_webhook?: boolean
  }) => api.put<NotificationPreferences>('/settings/notifications', data),
  testWebhook: () =>
    api.post<{ success: boolean; status_code: number | null; response_body: string; error_message: string | null }>(
      '/settings/notifications/test-webhook'
    ),
  notificationLogs: (params?: { delivery_type?: string; skip?: number; limit?: number }) =>
    api.get<{ items: NotificationLogItem[]; total: number }>('/settings/notification-logs', { params }),
  documents: () =>
    api.get<{
      documents: Array<{ id: string; name: string; uploaded_at: string | null }>
    }>('/settings/documents'),
  apiKeys: {
    list: () =>
      api.get<{ items: Array<{ id: string; name: string | null; key_prefix: string; expires_at: string | null; created_at: string }> }>('/api-keys'),
    create: (data: { name?: string | null; expires_at?: string | null }) =>
      api.post<{
        id: string
        name: string | null
        key_prefix: string
        key: string
        expires_at: string | null
        created_at: string
        message: string
      }>('/api-keys', data),
    revoke: (keyId: string) => api.delete<{ ok: boolean; message: string }>(`/api-keys/${keyId}`),
  },
}

export type InternalReceiptItem = {
  id: string
  receipt_number: string
  amount: string
  currency: string
  receipt_datetime: string | null
  items: Array<Record<string, unknown>> | null
  tax_amount: string | null
  reference_invoice_number: string | null
  reference_invoice_date: string | null
  internal_notes: string | null
  status: string
  pay_url: string
  qr_base64: string | null
  created_at: string
  is_paid: boolean
}

export const companyInternalReceipts = {
  list: (params?: { status?: string; skip?: number; limit?: number; include_qr?: boolean }) =>
    api.get<{ items: InternalReceiptItem[]; total: number }>('/internal-receipts', { params }),
  create: (data: {
    amount: number | string
    receipt_number?: string | null
    currency?: string
    receipt_datetime?: string | null
    items?: Array<Record<string, unknown>> | null
    tax_amount?: number | string | null
    reference_invoice_number?: string | null
    reference_invoice_date?: string | null
    internal_notes?: string | null
  }) =>
    api.post<{
      id: string
      receipt_number: string
      amount: string
      currency: string
      receipt_datetime: string | null
      items: unknown
      tax_amount: string | null
      reference_invoice_number: string | null
      reference_invoice_date: string | null
      internal_notes: string | null
      status: string
      pay_url: string
      qr_base64: string
      created_at: string
    }>('/internal-receipts', data),
  get: (receiptId: string, params?: { include_qr?: boolean }) =>
    api.get<InternalReceiptItem & { is_paid?: boolean }>(`/internal-receipts/${receiptId}`, { params }),
  archive: (receiptId: string) =>
    api.patch<{ id: string; status: string }>(`/internal-receipts/${receiptId}/archive`),
  delete: (receiptId: string) =>
    api.patch<{ id: string; status: string }>(`/internal-receipts/${receiptId}/delete`),
}

export const payoutReportUrl = (payoutId: string, format: 'pdf' | 'csv') => {
  return API_URL ? `${API_URL}/api/payouts/${payoutId}/report.${format}` : `/api/payouts/${payoutId}/report.${format}`
}
